import { CUSTOM_ELEMENTS_SCHEMA, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize, take } from 'rxjs';
import {
  RequerimentoOnlineDetalhe,
  RequerimentoOnlineDocumento,
} from '../../../core/models/requerimento-beneficio/requerimento-online-solicitacao.model';
import { EnumStatusResponse } from '../../../core/enum/enumHttpResponse';
import { RequerimentoOnlineSolicitacaoService } from '../../../core/services/requerimento-online-solicitacao.service';
import { ToastService } from '../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { RequerimentoStatusTagComponent } from '../../../shared/components/requerimento-status-tag/requerimento-status-tag.component';
import { ApiHttpError } from '../../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../../shared/services/loader/loader.service';

/** Motivo do reenvio e do erro da integração: até o tamanho da coluna na API (VARCHAR2(500)). */
export const MOTIVO_MINIMO = 5;
export const MOTIVO_MAXIMO = 500;

/** Status da análise do documento (`tp_status_analise`). */
const ANALISE_PENDENTE = ['P', 'E'];
const ANALISE_VALIDADO = 'V';
const ANALISE_RECUSADO = 'N';

/** Status da solicitação depois de uma ação, para o texto do toast. */
const STATUS_EM_PROCESSAMENTO = 'L';
const STATUS_ERRO_INTEGRACAO = 'F';

/** Um documento pendente no modal de reenvio: marcado ou não e, se marcado, o motivo. */
type ReenvioDocumentoForm = FormGroup<{
  idDocumento: FormControl<number>;
  marcado: FormControl<boolean>;
  motivo: FormControl<string>;
}>;

@Component({
  selector: 'app-detalhe-requerimento',
  imports: [ReactiveFormsModule, VivestModalAreaVisivelDirective, RequerimentoStatusTagComponent],
  templateUrl: './detalhe-requerimento.component.html',
  styleUrl: './detalhe-requerimento.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class DetalheRequerimentoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly requerimentoOnlineSolicitacaoService = inject(RequerimentoOnlineSolicitacaoService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  private readonly solicitacaoSignal = signal<RequerimentoOnlineDetalhe | null>(null);
  readonly solicitacao = this.solicitacaoSignal.asReadonly();
  readonly documentos = computed(() => this.solicitacao()?.documentos ?? []);
  /**
   * Mudanças de status da mais recente para a mais antiga (data ISO compara como texto). No mesmo segundo (o aprovar
   * grava V e L juntos), desempata pelo id: a linha gravada depois vem primeiro.
   */
  readonly historico = computed(() =>
    [...(this.solicitacao()?.historico ?? [])].sort(
      (a, b) => b.alteradoEm.localeCompare(a.alteradoEm) || b.idHistorico - a.idHistorico,
    ),
  );
  /** Aprovar ou pedir reenvio: a API diz quando (Solicitado e Documento reenviado, também sem documento). */
  readonly podeValidarDocumentos = computed(() => !!this.solicitacao()?.permiteAnalisarDocumentos);
  /** Documentos que ainda aguardam a decisão do analista: os únicos que o reenvio pode recusar. */
  readonly pendentes = computed(() => this.documentos().filter((documento) => this.pendente(documento)));
  /** Documentos que o participante reenviou e aguardam nova análise. */
  readonly documentosReenviados = computed(() => this.pendentes().filter((documento) => documento.reenvio));
  /** "Comprovante de residência" ou "Comprovante de residência e Laudo médico pericial". */
  readonly nomesReenviados = computed(() => {
    const nomes = this.documentosReenviados().map((documento) => documento.tipoDocumento);
    return nomes.length > 1 ? `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}` : (nomes[0] ?? '');
  });
  /** Um documento só para validar: os textos das ações ficam no singular. */
  readonly documentoUnico = computed(() => this.pendentes().length === 1);
  readonly semDocumento = computed(() => this.documentos().length === 0);
  readonly podeConcluir = computed(() => !!this.solicitacao()?.permiteConcluirSolicitacao);
  readonly podeRegistrarErro = computed(() => !!this.solicitacao()?.permiteRegistrarErro);
  readonly podeReprocessar = computed(() => !!this.solicitacao()?.permiteReprocessar);
  readonly naoEncontrada = signal(false);
  readonly formatDate = formatDateIsoToBr;
  readonly motivoMaximo = MOTIVO_MAXIMO;

  readonly confirmacaoAberta = signal(false);
  readonly reenvioAberto = signal(false);
  readonly concluirAberto = signal(false);
  readonly erroIntegracaoAberto = signal(false);
  readonly reprocessarAberto = signal(false);

  /** Documento aberto no modal de visualização; `null` com o modal fechado. */
  readonly documentoVisualizado = signal<RequerimentoOnlineDocumento | null>(null);
  /** URL de objeto do arquivo visualizado (o arquivo exige token: não há link direto). Revogada ao fechar. */
  readonly urlArquivo = signal<string | null>(null);
  /** No `src` do iframe, que exige URL confiável; o PDF abre na largura do modal (`#view=FitH`). */
  readonly urlVisualizacao = computed<SafeResourceUrl | null>(() => {
    const url = this.urlArquivo();
    const documento = this.documentoVisualizado();
    if (!url || !documento) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(documento.contentType === 'application/pdf' ? `${url}#view=FitH` : url);
  });

  /** Pedido de reenvio: um grupo por documento pendente; o motivo é obrigatório só no marcado. */
  readonly reenvioForm = this.fb.nonNullable.group({
    documentos: this.fb.array<ReenvioDocumentoForm>([]),
  });

  /** "Registrar erro": o motivo que o corporativo apontou. */
  readonly erroIntegracaoForm = this.fb.nonNullable.group({
    motivo: ['', [Validators.required, Validators.minLength(MOTIVO_MINIMO), Validators.maxLength(MOTIVO_MAXIMO)]],
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.revogarArquivo());
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.naoEncontrada.set(true);
      return;
    }

    this.carregarSolicitacao(id);
  }

  /** `2026-10-01T09:15:00` → `01/10/2026 às 09:15`; sem hora, só a data. */
  dataHora(iso: string): string {
    const [data, hora = ''] = iso.split(/[T ]/);
    const dataBr = formatDateIsoToBr(data);
    return hora ? `${dataBr} às ${hora.slice(0, 5)}` : dataBr;
  }

  pendente(documento: RequerimentoOnlineDocumento): boolean {
    return ANALISE_PENDENTE.includes(documento.tpStatusAnalise);
  }

  reenviado(documento: RequerimentoOnlineDocumento): boolean {
    return this.pendente(documento) && documento.reenvio;
  }

  validado(documento: RequerimentoOnlineDocumento): boolean {
    return documento.tpStatusAnalise === ANALISE_VALIDADO;
  }

  rejeitado(documento: RequerimentoOnlineDocumento): boolean {
    return documento.tpStatusAnalise === ANALISE_RECUSADO;
  }

  voltar(): void {
    void this.router.navigate(['/requerimento-online/solicitacoes']);
  }

  // ── arquivo do documento ───────────────────────────────────────────────────

  /** Baixa o arquivo com o nome original. */
  baixar(documento: RequerimentoOnlineDocumento): void {
    this.carregarArquivo(documento, (url) => {
      this.dispararDownload(url, documento.nomeArquivo);
      URL.revokeObjectURL(url);
    });
  }

  abrirVisualizacao(documento: RequerimentoOnlineDocumento): void {
    this.carregarArquivo(documento, (url) => {
      this.revogarArquivo();
      this.urlArquivo.set(url);
      this.documentoVisualizado.set(documento);
    });
  }

  fecharVisualizacao(): void {
    this.documentoVisualizado.set(null);
    this.revogarArquivo();
  }

  /** "Baixar" de dentro do modal: reaproveita o arquivo já carregado. */
  baixarVisualizado(): void {
    const url = this.urlArquivo();
    const documento = this.documentoVisualizado();
    if (url && documento) {
      this.dispararDownload(url, documento.nomeArquivo);
    }
  }

  // ── aprovação ───────────────────────────────────────────────────────────────

  abrirConfirmacao(): void {
    this.confirmacaoAberta.set(true);
  }

  fecharConfirmacao(): void {
    this.confirmacaoAberta.set(false);
  }

  /** Aprova os pendentes; a API já envia ao corporativo (em processamento, ou erro com o motivo). */
  aprovar(): void {
    this.confirmacaoAberta.set(false);
    this.executarAcao(
      (id) => this.requerimentoOnlineSolicitacaoService.aprovarDocumentos(id),
      (detalhe) =>
        detalhe.tpStatus === STATUS_ERRO_INTEGRACAO
          ? this.toastService.show(`Documentos aprovados, mas o envio ao corporativo falhou: ${detalhe.motivoErroIntegracao}`, 'error')
          : this.toastService.show(
              detalhe.tpStatus === STATUS_EM_PROCESSAMENTO
                ? 'Documentos aprovados. Solicitação enviada para processamento.'
                : 'Documentos aprovados.',
              'success',
            ),
    );
  }

  // ── pedido de reenvio ───────────────────────────────────────────────────────

  get reenvioDocumentos(): FormArray<ReenvioDocumentoForm> {
    return this.reenvioForm.controls.documentos;
  }

  /** Um grupo por documento pendente; com um documento só, ele já vem marcado: não há o que escolher. */
  abrirReenvio(): void {
    const unico = this.documentoUnico();
    this.reenvioDocumentos.clear();
    for (const documento of this.pendentes()) {
      this.reenvioDocumentos.push(
        this.fb.nonNullable.group({
          idDocumento: documento.idDocumento,
          marcado: unico,
          motivo: [''],
        }),
      );
    }
    this.atualizarValidacaoMotivos();
    this.reenvioAberto.set(true);
  }

  fecharReenvio(): void {
    this.reenvioAberto.set(false);
  }

  tipoDocumento(idDocumento: number): string {
    return this.documentos().find((documento) => documento.idDocumento === idDocumento)?.tipoDocumento ?? '';
  }

  alternarDocumento(indice: number, marcado: boolean): void {
    const grupo = this.reenvioDocumentos.at(indice);
    grupo.controls.marcado.setValue(marcado);
    if (!marcado) {
      grupo.controls.motivo.reset('');
    }
    this.atualizarValidacaoMotivos();
  }

  /** Ao menos um documento marcado. */
  nenhumMarcado(): boolean {
    return !this.reenvioDocumentos.controls.some((grupo) => grupo.controls.marcado.value);
  }

  /** Mensagem do motivo de um documento marcado, só depois que o campo foi tocado. */
  erroMotivo(indice: number): string | null {
    return this.mensagemMotivo(this.reenvioDocumentos.at(indice).controls.motivo);
  }

  enviarReenvio(): void {
    this.reenvioForm.markAllAsTouched();
    if (this.nenhumMarcado() || this.reenvioForm.invalid) {
      return;
    }

    const itens = this.reenvioDocumentos.controls
      .filter((grupo) => grupo.controls.marcado.value)
      .map((grupo) => ({ idDocumento: grupo.controls.idDocumento.value, motivo: grupo.controls.motivo.value }));

    this.reenvioAberto.set(false);
    this.executarAcao(
      (id) => this.requerimentoOnlineSolicitacaoService.solicitarReenvio(id, itens),
      () => this.toastService.show('Pedido de reenvio enviado ao participante.', 'success'),
    );
  }

  // ── integração com o corporativo ───────────────────────────────────────────

  abrirConcluir(): void {
    this.concluirAberto.set(true);
  }

  fecharConcluir(): void {
    this.concluirAberto.set(false);
  }

  /** "Aprovar solicitação": o operador conferiu no corporativo que o processo foi concluído. */
  concluir(): void {
    this.concluirAberto.set(false);
    this.executarAcao(
      (id) => this.requerimentoOnlineSolicitacaoService.concluirSolicitacao(id),
      () => this.toastService.show('Solicitação concluída.', 'success'),
    );
  }

  abrirRegistrarErro(): void {
    this.erroIntegracaoForm.reset({ motivo: '' });
    this.erroIntegracaoAberto.set(true);
  }

  fecharRegistrarErro(): void {
    this.erroIntegracaoAberto.set(false);
  }

  erroMotivoIntegracao(): string | null {
    return this.mensagemMotivo(this.erroIntegracaoForm.controls.motivo);
  }

  registrarErro(): void {
    if (this.erroIntegracaoForm.invalid) {
      this.erroIntegracaoForm.markAllAsTouched();
      return;
    }

    const motivo = this.erroIntegracaoForm.controls.motivo.value;
    this.erroIntegracaoAberto.set(false);
    this.executarAcao(
      (id) => this.requerimentoOnlineSolicitacaoService.registrarErroIntegracao(id, motivo),
      () => this.toastService.show('Erro registrado na solicitação.', 'success'),
    );
  }

  abrirReprocessar(): void {
    this.reprocessarAberto.set(true);
  }

  fecharReprocessar(): void {
    this.reprocessarAberto.set(false);
  }

  reprocessar(): void {
    this.reprocessarAberto.set(false);
    this.executarAcao(
      (id) => this.requerimentoOnlineSolicitacaoService.reprocessarIntegracao(id),
      (detalhe) =>
        detalhe.tpStatus === STATUS_ERRO_INTEGRACAO
          ? this.toastService.show(`O envio ao corporativo falhou de novo: ${detalhe.motivoErroIntegracao}`, 'error')
          : this.toastService.show('Solicitação enviada novamente para processamento.', 'success'),
    );
  }

  // ── apoio ───────────────────────────────────────────────────────────────────

  private carregarSolicitacao(id: number | string): void {
    this.loaderService.showLoader();

    this.requerimentoOnlineSolicitacaoService
      .obterSolicitacao(id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (solicitacao) => this.solicitacaoSignal.set(solicitacao),
        error: () => {
          this.naoEncontrada.set(true);
          this.toastService.show('Não foi possível carregar a solicitação.', 'error');
        },
      });
  }

  /**
   * Executa uma ação do backoffice e troca o detalhe pelo que a API devolveu. 409 (a solicitação mudou de status,
   * documento já analisado): mostra a mensagem da API e recarrega, para a tela refletir a situação atual.
   */
  private executarAcao(
    acao: (id: number) => Observable<RequerimentoOnlineDetalhe>,
    aoConcluir: (detalhe: RequerimentoOnlineDetalhe) => void,
  ): void {
    const id = this.solicitacao()?.idSolicitacao;
    if (id == null) {
      return;
    }

    this.loaderService.showLoader();
    acao(id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (detalhe) => {
          this.solicitacaoSignal.set(detalhe);
          aoConcluir(detalhe);
        },
        error: (erro: unknown) => {
          if (erro instanceof ApiHttpError && erro.status === EnumStatusResponse.Conflict) {
            this.toastService.show(erro.message, 'error');
            this.carregarSolicitacao(id);
            return;
          }
          this.toastService.show('Não foi possível concluir a ação. Tente novamente.', 'error');
        },
      });
  }

  private carregarArquivo(documento: RequerimentoOnlineDocumento, aoCarregar: (url: string) => void): void {
    const id = this.solicitacao()?.idSolicitacao;
    if (id == null) {
      return;
    }

    this.loaderService.showLoader();
    this.requerimentoOnlineSolicitacaoService
      .obterArquivo(id, documento.idDocumento)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (arquivo) => aoCarregar(URL.createObjectURL(arquivo)),
        error: () => this.toastService.show('Não foi possível abrir o documento.', 'error'),
      });
  }

  private dispararDownload(url: string, nomeArquivo: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    link.click();
  }

  private revogarArquivo(): void {
    const url = this.urlArquivo();
    if (url) {
      URL.revokeObjectURL(url);
      this.urlArquivo.set(null);
    }
  }

  /** O motivo só é exigido no documento marcado. */
  private atualizarValidacaoMotivos(): void {
    for (const grupo of this.reenvioDocumentos.controls) {
      const motivo = grupo.controls.motivo;
      motivo.setValidators(
        grupo.controls.marcado.value
          ? [Validators.required, Validators.minLength(MOTIVO_MINIMO), Validators.maxLength(MOTIVO_MAXIMO)]
          : [],
      );
      motivo.updateValueAndValidity({ emitEvent: false });
    }
  }

  private mensagemMotivo(controle: FormControl<string>): string | null {
    if (!controle.touched || controle.valid) {
      return null;
    }
    return controle.hasError('required') ? 'Informe o motivo.' : `O motivo deve ter no mínimo ${MOTIVO_MINIMO} caracteres.`;
  }
}
